import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { apiErrorMessage } from "../lib/inventoryApi";
import { formatDateTime } from "../lib/format";
import { konfirmasi } from "../lib/dialogAplikasi";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

/* K207 / W5 (SESI-58 U2) — KANAL WHATSAPP, Owner saja.
   Tiap nomor WhatsApp (inbox Cekat, per wilayah) punya: akun bot ber-peran CS (tulisan AI tercatat atas namanya,
   cabang bawaan = cabang akun itu) dan KUNCI alat AI. Kunci baru ditampilkan SEKALI di layar ini — Owner
   menyalinnya sendiri ke pengaturan Cekat (API Tools). Yang tersimpan di ETAMSIOS hanya sidik kuncinya; kunci lama
   langsung tidak berlaku begitu kunci baru dibuat.
   Sumber: GET /api/wa-kanal/inbox, GET …/calon-akun-bot, PUT …/akun-bot, POST …/kunci-baru, POST …/cabut-kunci.

   K213 (Owner 12 Okt 2026): lewat ETAMSIOS alat AI hanya melayani dua nomor Owner sampai Owner membuka untuk semua
   nomor sesudah uji coba dinyatakan siap 100% dan teruji — berlaku juga saat aplikasi dipakai sungguhan. Kartu
   SaklarSemuaNomor: GET/POST /api/wa-kanal/saklar-semua-nomor (Owner saja, alasan wajib, riwayat tercatat). */

const ALASAN_MIN = 10;
const NAMA_ALAT = {
  "buat-customer-dr": "catat pesanan",
  "minta-link-tanda-jadi": "tautan tanda jadi",
  "status-pesanan": "status pesanan",
  "terbitkan-ulang-link": "tautan Ruang Pesanan baru",
  eskalasi: "teruskan ke CS",
};

function SaklarSemuaNomor({ inbox }) {
  const [data, setData] = useState(null);
  const [alasan, setAlasan] = useState("");
  const [sibuk, setSibuk] = useState(false);

  const muat = useCallback(async () => {
    try { const r = await api.get("/wa-kanal/saklar-semua-nomor"); setData(r.data); }
    catch { setData({ galat: true }); }
  }, []);
  useEffect(() => { muat(); }, [muat]);

  if (!data) return null;
  const labelInbox = Object.fromEntries((inbox || []).map((ib) => [ib.inbox_id, ib.label || ib.region || "Nomor WhatsApp"]));
  const dibuka = data.semua_nomor_dibuka === true;
  const teks = alasan.trim();

  async function ubah() {
    const lanjut = await konfirmasi(
      dibuka
        ? "Tutup kembali? Alat AI ETAMSIOS hanya melayani nomor Owner lagi. Customer lain tetap dilayani AI Cekat seperti biasa, tanpa catat pesanan atau tautan dari ETAMSIOS."
        : "Buka alat AI untuk SEMUA nomor? Lakukan hanya bila uji coba sudah dinyatakan siap 100% melayani customer dan benar-benar teruji. Sesudah dibuka, customer sungguhan bisa membuat pesanan dan menerima tautan pembayaran lewat AI.",
      { judul: dibuka ? "Tutup untuk nomor lain" : "Buka untuk semua nomor", ya: dibuka ? "Tutup" : "Buka", bahaya: !dibuka });
    if (!lanjut) return;
    setSibuk(true);
    try {
      await api.post("/wa-kanal/saklar-semua-nomor", { nyala: !dibuka, alasan: teks });
      toast.success(dibuka ? "Alat AI kembali hanya untuk nomor Owner." : "Alat AI dibuka untuk semua nomor.");
      setAlasan("");
      muat();
    } catch (e) { toast.error(apiErrorMessage(e, "Saklar belum bisa diubah.")); }
    finally { setSibuk(false); }
  }

  return (
    <Card className="p-4 space-y-3" data-testid="kanal-wa-saklar-semua-nomor">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-semibold">Nomor yang dilayani alat AI</h2>
        {data.galat || data.terbaca === false
          ? <Badge variant="outline">Hanya nomor Owner</Badge>
          : <Badge variant={dibuka ? "default" : "secondary"} data-testid="kanal-wa-saklar-status">
              {dibuka ? "Semua nomor" : "Hanya nomor Owner"}
            </Badge>}
      </div>
      <p className="text-sm text-muted-foreground">
        Keputusan Owner 12 Oktober 2026: lewat ETAMSIOS, AI di Cekat hanya melayani nomor
        {" "}{(data.nomor_izin || []).join(" dan ") || "milik Owner"}. Customer lain tetap dilayani AI
        Cekat seperti biasa, tanpa catat pesanan, tautan pembayaran, atau status pesanan dari ETAMSIOS. Buka untuk semua
        nomor hanya setelah uji coba dinyatakan siap 100% dan benar-benar teruji. Berlaku juga saat aplikasi sudah dipakai
        sungguhan.
      </p>
      {(data.galat || data.terbaca === false) ? (
        <p className="text-sm text-muted-foreground">
          Keadaan saklar belum bisa dimuat. Selama itu alat AI tetap hanya untuk nomor Owner. Coba muat ulang halaman.
        </p>
      ) : (
        <>
          {data.alasan && (
            <p className="text-xs text-muted-foreground">
              Terakhir: {data.alasan}{data.diubah_at ? ` (${formatDateTime(data.diubah_at)}` : ""}
              {data.diubah_at ? `${data.diubah_oleh_email ? `, ${data.diubah_oleh_email}` : ""})` : ""}.
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Input className="max-w-sm" placeholder={`Alasan (minimal ${ALASAN_MIN} huruf)`} value={alasan}
                   onChange={(e) => setAlasan(e.target.value)} data-testid="kanal-wa-saklar-alasan" />
            <Button size="sm" variant={dibuka ? "default" : "destructive"} disabled={sibuk || teks.length < ALASAN_MIN}
                    onClick={ubah} data-testid="kanal-wa-saklar-ubah">
              {sibuk ? "Menyimpan…" : dibuka ? "Tutup, kembali hanya nomor Owner" : "Buka untuk semua nomor"}
            </Button>
          </div>
        </>
      )}
      <div className="text-sm space-y-1">
        <p className="font-medium">Riwayat saklar</p>
        {data.riwayat == null ? <p className="text-xs text-muted-foreground">Riwayat belum bisa dimuat.</p>
          : data.riwayat.length === 0 ? <p className="text-xs text-muted-foreground">Belum pernah diubah.</p>
          : (
            <ul className="text-xs space-y-1" data-testid="kanal-wa-saklar-riwayat">
              {data.riwayat.map((h, i) => (
                <li key={`${h.waktu}-${i}`}>
                  {formatDateTime(h.waktu)} · {h.dibuka ? "dibuka untuk semua nomor" : "ditutup, hanya nomor Owner"}
                  {h.oleh ? ` · ${h.oleh}` : ""}{h.alasan ? ` — ${h.alasan}` : ""}
                </li>
              ))}
            </ul>
          )}
      </div>
      <div className="text-sm space-y-1">
        <p className="font-medium">Nomor lain yang mencoba (tidak dilayani ETAMSIOS)</p>
        {data.percobaan_tertahan == null ? <p className="text-xs text-muted-foreground">Catatan belum bisa dimuat.</p>
          : data.percobaan_tertahan.length === 0 ? <p className="text-xs text-muted-foreground">Belum ada.</p>
          : (
            <ul className="text-xs space-y-1" data-testid="kanal-wa-saklar-percobaan">
              {data.percobaan_tertahan.map((c, i) => (
                <li key={`${c.waktu}-${i}`}>
                  {formatDateTime(c.waktu)} · nomor berakhiran {String(c.nomor_akhir || "").replace("…", "")}
                  {" "}· {NAMA_ALAT[c.alat] || "alat AI"} · {labelInbox[c.inbox_id] || "nomor WhatsApp"}
                  {c.tertahan_sejak_catatan_lalu ? ` (+${c.tertahan_sejak_catatan_lalu} panggilan serupa sebelumnya)` : ""}
                </li>
              ))}
            </ul>
          )}
      </div>
    </Card>
  );
}

function BarisInbox({ ib, muatUlang }) {
  const [calon, setCalon] = useState(null);
  const [pilih, setPilih] = useState(ib.akun_bot?.id || "");
  const [kunci, setKunci] = useState(null);
  const [sibuk, setSibuk] = useState(false);

  async function bukaCalon() {
    try {
      const { data } = await api.get(`/wa-kanal/inbox/${ib.inbox_id}/calon-akun-bot`);
      setCalon(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(apiErrorMessage(e, "Daftar akun CS gagal dimuat.")); }
  }

  async function simpanBot() {
    setSibuk(true);
    try {
      await api.put(`/wa-kanal/inbox/${ib.inbox_id}/akun-bot`, { user_id: pilih });
      toast.success("Akun bot disimpan.");
      setCalon(null);
      muatUlang();
    } catch (e) { toast.error(apiErrorMessage(e, "Akun bot belum bisa disimpan.")); }
    finally { setSibuk(false); }
  }

  async function kunciBaru() {
    const lanjut = await konfirmasi(
      ib.kunci_ada
        ? "Kunci lama untuk nomor ini LANGSUNG tidak berlaku. AI di Cekat berhenti bisa memakai alat sampai kunci baru ditempel di pengaturan Cekat. Lanjutkan?"
        : "Buat kunci alat AI untuk nomor ini? Kunci hanya ditampilkan sekali.",
      { judul: "Buat kunci baru", ya: "Buat kunci", bahaya: ib.kunci_ada });
    if (!lanjut) return;
    setSibuk(true);
    try {
      const { data } = await api.post(`/wa-kanal/inbox/${ib.inbox_id}/kunci-baru`);
      setKunci(data);
      muatUlang();
    } catch (e) { toast.error(apiErrorMessage(e, "Kunci baru belum bisa dibuat.")); }
    finally { setSibuk(false); }
  }

  async function cabut() {
    const lanjut = await konfirmasi("Matikan alat AI untuk nomor ini? Semua panggilan AI dari Cekat akan ditolak sampai kunci baru dibuat.",
      { judul: "Matikan alat AI", ya: "Matikan", bahaya: true });
    if (!lanjut) return;
    try {
      await api.post(`/wa-kanal/inbox/${ib.inbox_id}/cabut-kunci`);
      toast.success("Alat AI untuk nomor ini dimatikan.");
      muatUlang();
    } catch (e) { toast.error(apiErrorMessage(e, "Kunci belum bisa dicabut.")); }
  }

  async function salin() {
    try { await navigator.clipboard.writeText(kunci.kunci); toast.success("Kunci disalin."); }
    catch { toast.error("Salin otomatis tidak tersedia — pilih teks kuncinya lalu salin manual."); }
  }

  return (
    <Card className="p-4 space-y-3" data-testid={`kanal-wa-${ib.inbox_id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-semibold">{ib.label || "Nomor WhatsApp"}</h2>
        <Badge variant="outline">Wilayah {ib.region || "-"}</Badge>
        {ib.aktif ? <Badge variant="secondary">Aktif</Badge> : <Badge variant="outline">Nonaktif</Badge>}
      </div>

      <div className="text-sm space-y-1">
        <p>
          <span className="text-muted-foreground">Akun bot: </span>
          {ib.akun_bot ? `${ib.akun_bot.nama} (${ib.akun_bot.email}) · cabang bawaan ${ib.akun_bot.cabang || "-"}` : "Belum dipilih"}
        </p>
        {calon === null ? (
          <Button size="sm" variant="outline" onClick={bukaCalon} data-testid="kanal-wa-ganti-bot">
            {ib.akun_bot ? "Ganti akun bot" : "Pilih akun bot"}
          </Button>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <select className="rounded-md border border-border bg-background px-2 py-1 text-sm" value={pilih}
                    onChange={(e) => setPilih(e.target.value)} data-testid="kanal-wa-pilih-bot">
              <option value="">— pilih akun CS —</option>
              {calon.map((c) => <option key={c.id} value={c.id}>{c.nama} · {c.cabang}</option>)}
            </select>
            <Button size="sm" disabled={!pilih || sibuk} onClick={simpanBot}>Simpan</Button>
            <Button size="sm" variant="ghost" onClick={() => setCalon(null)}>Batal</Button>
            {calon.length === 0 && <span className="text-xs text-muted-foreground">Belum ada akun CS aktif di wilayah ini.</span>}
          </div>
        )}
      </div>

      <div className="text-sm space-y-1">
        <p>
          <span className="text-muted-foreground">Kunci alat AI: </span>
          {ib.kunci_ada ? `dibuat ${formatDateTime(ib.kunci_dibuat_at)}${ib.kunci_dibuat_oleh ? ` oleh ${ib.kunci_dibuat_oleh}` : ""}` : "Belum ada"}
        </p>
        <p>
          <span className="text-muted-foreground">Terakhir dipakai AI: </span>
          {ib.terakhir_dipakai_at ? formatDateTime(ib.terakhir_dipakai_at) : "Belum pernah"}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant={ib.kunci_ada ? "outline" : "default"} disabled={sibuk || !ib.akun_bot}
                  onClick={kunciBaru} data-testid="kanal-wa-kunci-baru">Buat kunci baru</Button>
          {ib.kunci_ada && <Button size="sm" variant="ghost" onClick={cabut} data-testid="kanal-wa-cabut">Matikan alat AI</Button>}
        </div>
        {!ib.akun_bot && <p className="text-xs text-muted-foreground">Pilih akun bot dulu sebelum membuat kunci.</p>}
      </div>

      {kunci && (
        <div className="rounded-lg border border-warning-soft-border bg-warning-soft p-3 text-sm space-y-2" data-testid="kanal-wa-kunci-tampil">
          <p className="font-medium text-warning-soft-foreground">
            Salin kunci ini sekarang. Setelah layar ditutup, kunci tidak bisa dilihat lagi.
          </p>
          <p className="text-xs">
            Tempel di Cekat: Devtools › API Tools › tiap alat cabang ini › kolom <b>API Key (Bearer)</b>, pilih
            <b> {kunci.nama_header}</b>, lalu isi dengan kunci ini.
          </p>
          <code className="block break-all rounded bg-background px-2 py-1 text-xs">{kunci.kunci}</code>
          <div className="flex gap-2">
            <Button size="sm" onClick={salin}>Salin kunci</Button>
            <Button size="sm" variant="ghost" onClick={() => setKunci(null)}>Sudah saya salin, tutup</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function MasterKanalWa() {
  const [inbox, setInbox] = useState([]);
  const [muat, setMuat] = useState(true);

  const muatUlang = useCallback(async () => {
    setMuat(true);
    try {
      const { data } = await api.get("/wa-kanal/inbox");
      setInbox(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(apiErrorMessage(e, "Daftar nomor WhatsApp gagal dimuat.")); }
    finally { setMuat(false); }
  }, []);
  useEffect(() => { muatUlang(); }, [muatUlang]);

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="master-kanal-wa">
      <div>
        <h1 className="text-xl font-semibold">Alat AI WhatsApp</h1>
        <p className="text-sm text-muted-foreground mt-1">
          AI di Cekat melayani customer lewat lima alat ETAMSIOS (catat pesanan, tautan tanda jadi, status pesanan,
          tautan Ruang Pesanan baru, teruskan ke CS). Tiap nomor butuh akun bot CS dan kunci yang Anda tempel sendiri
          di pengaturan Cekat. AI tidak pernah bisa mengesahkan pembayaran, desain, harga, atau persetujuan pesanan.
        </p>
      </div>
      <SaklarSemuaNomor inbox={inbox} />
      {muat && <p className="text-sm text-muted-foreground">Memuat…</p>}
      {!muat && inbox.length === 0 && <p className="text-sm text-muted-foreground">Belum ada nomor WhatsApp terdaftar.</p>}
      {inbox.map((ib) => <BarisInbox key={ib.inbox_id} ib={ib} muatUlang={muatUlang} />)}
    </div>
  );
}
